import WorkApprovalButton from './WorkApprovalButton';
import WorkOrchestratorControls from './WorkOrchestratorControls';
import WorkProjectFolderButton from './WorkProjectFolderButton';
import * as DropdownMenu from '@/components/DropdownMenu/DropdownMenu';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Modal,
  Platform,
  Pressable,
  StyleSheet,
  Switch,
  ScrollView,
  View,
  useWindowDimensions,
} from 'react-native';
import PropTypes from 'prop-types';
import { useTheme } from '@/context/ThemeContext';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import Tooltip from '@/components/Tooltip';
import { useChatStore, useCoworkerStore, useToastStore } from '@/store';
import { coworkerApi } from '@/lib/api';
import useTranslation from '@/hooks/useTranslation';
import useResponsiveness from '@/hooks/useResponsiveness';
import { isWorkActive } from '@/constants/work';
import {
  WORK_THINKING_LEVELS,
  WORK_THINKING_TRACK_COLOR,
  clampThinkingIndex,
  thinkingPowerById,
} from '@/constants/workThinkingPower';

const TRACK_HEIGHT = 10;
const THUMB_SIZE = 18;
const DOT_SIZE = 4;
const SLIDER_HEIGHT = 28;
// Keep the entire thumb and its shadow inside the menu's clipping bounds.
const SLIDER_EDGE_INSET = THUMB_SIZE / 2 + 4;

function clientXFromEvent(event) {
  if (typeof event?.clientX === 'number') return event.clientX;
  const native = event?.nativeEvent;
  if (typeof native?.clientX === 'number') return native.clientX;
  if (typeof native?.pageX === 'number') return native.pageX;
  return null;
}

function rectFromEvent(event) {
  const nodes = [
    event?.currentTarget,
    event?.target,
    event?.nativeEvent?.currentTarget,
    event?.nativeEvent?.target,
  ];
  for (const node of nodes) {
    if (node && typeof node.getBoundingClientRect === 'function') {
      return node.getBoundingClientRect();
    }
  }
  return null;
}

function SettingHelpButton({ label, description, expanded, onPress }) {
  const { colors } = useTheme();
  return (
    // React Native's web Modal sits at 9999; help must render above it.
    <Tooltip label={description} disabled={expanded} showIsXs align="start" zIndex={10000}>
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={label}
        accessibilityHint={description}
        accessibilityState={{ expanded }}
        {...(Platform.OS === 'web' ? { 'aria-expanded': expanded } : {})}
        onPress={(event) => {
          event?.stopPropagation?.();
          onPress();
        }}
        style={({ hovered, pressed }) => [
          styles.helpButton,
          (hovered || pressed || expanded) && { backgroundColor: colors.solid_color_tertiary },
        ]}
      >
        <Icon name="circle-info" size={13} color={colors.text_color_secondary} />
      </Pressable>
    </Tooltip>
  );
}

function WorkThinkingPowerPanel({ level, onChange, colors }) {
  const { t } = useTranslation();
  const isMac = Platform.OS === 'macos';
  const sliderHeight = isMac ? 44 : SLIDER_HEIGHT;
  const [showHelp, setShowHelp] = useState(false);
  const max = WORK_THINKING_LEVELS.length - 1;
  const draggingRef = useRef(false);
  const levelIdRef = useRef(level.id);
  const previewIndexRef = useRef(null);
  const [previewIndex, setPreviewIndex] = useState(null);
  const [focusedIndex, setFocusedIndex] = useState(null);
  const [sliderWidth, setSliderWidth] = useState(0);
  levelIdRef.current = level.id;

  const shown = previewIndex == null
    ? level
    : thinkingPowerById(WORK_THINKING_LEVELS[clampThinkingIndex(previewIndex)]?.id);
  const fill = max === 0 ? 1 : shown.index / max;
  const trackWidth = Math.max(0, sliderWidth - SLIDER_EDGE_INSET * 2);

  useEffect(() => {
    previewIndexRef.current = null;
    setPreviewIndex(null);
  }, [level.id]);

  const previewAt = useCallback((index) => {
    const next = clampThinkingIndex(index);
    previewIndexRef.current = next;
    setPreviewIndex(next);
  }, []);

  const commitIndex = useCallback((index) => {
    const next = thinkingPowerById(WORK_THINKING_LEVELS[clampThinkingIndex(index)]?.id);
    if (next && next.id !== levelIdRef.current) {
      onChange(next);
      return;
    }
    previewIndexRef.current = null;
    setPreviewIndex(null);
  }, [onChange]);

  const pickFromEvent = useCallback((event) => {
    const rect = rectFromEvent(event);
    const clientX = clientXFromEvent(event);
    if (rect?.width > 0 && Number.isFinite(clientX)) {
      previewAt(((clientX - rect.left - SLIDER_EDGE_INSET) / Math.max(1, rect.width - SLIDER_EDGE_INSET * 2)) * max);
      return;
    }
    const locationX = event?.nativeEvent?.locationX;
    const width = event?.currentTarget?.offsetWidth
      || event?.nativeEvent?.layout?.width
      || sliderWidth;
    if (Number.isFinite(locationX) && width > 0) {
      previewAt(((locationX - SLIDER_EDGE_INSET) / Math.max(1, width - SLIDER_EDGE_INSET * 2)) * max);
    }
  }, [max, previewAt, sliderWidth]);

  const finishDrag = useCallback((event) => {
    const wasDragging = draggingRef.current;
    draggingRef.current = false;
    // Only release a capture we still hold: the mouse fallback events carry no
    // pointerId, and the browser fires a synthetic mouseleave after pointerup
    // has already released it.
    const node = event?.currentTarget;
    const pointerId = event?.pointerId;
    if (Number.isFinite(pointerId) && node?.hasPointerCapture?.(pointerId)) {
      node.releasePointerCapture(pointerId);
    }
    if (!wasDragging) return;
    if (previewIndexRef.current != null) commitIndex(previewIndexRef.current);
  }, [commitIndex]);

  const webPointer = Platform.OS === 'web' ? {
    tabIndex: 0,
    'aria-valuemin': 0,
    'aria-valuemax': max,
    'aria-valuenow': shown.index,
    'aria-valuetext': shown.label,
    onPointerDown: (event) => {
      event.preventDefault();
      event.stopPropagation();
      draggingRef.current = true;
      event.currentTarget?.setPointerCapture?.(event.pointerId);
      pickFromEvent(event);
    },
    onPointerMove: (event) => {
      if (!draggingRef.current) return;
      pickFromEvent(event);
    },
    onPointerUp: finishDrag,
    onPointerCancel: finishDrag,
    onMouseDown: (event) => {
      event.preventDefault();
      event.stopPropagation();
      draggingRef.current = true;
      pickFromEvent(event);
    },
    onMouseMove: (event) => {
      if (!draggingRef.current) return;
      pickFromEvent(event);
    },
    onMouseUp: finishDrag,
    onMouseLeave: (event) => {
      if (event.buttons) return;
      finishDrag(event);
    },
    onKeyDown: (event) => {
      if (event.key === 'ArrowRight' || event.key === 'ArrowUp') {
        event.preventDefault();
        commitIndex(shown.index + 1);
      } else if (event.key === 'ArrowLeft' || event.key === 'ArrowDown') {
        event.preventDefault();
        commitIndex(shown.index - 1);
      }
    },
  } : {};

  const levelButtons = (
    <View style={isMac ? styles.macStepRow : styles.stepRow} pointerEvents="box-none">
      {WORK_THINKING_LEVELS.map((item) => (
        <Pressable
          key={item.id}
          accessibilityRole="button"
          accessibilityLabel={item.label}
          accessibilityState={{ selected: item.id === shown.id }}
          {...(isMac ? {
            enableFocusRing: false,
            onFocus: () => setFocusedIndex(item.index),
            onBlur: () => setFocusedIndex(null),
          } : {})}
          onPress={() => commitIndex(item.index)}
          style={({ hovered, pressed }) => isMac ? [styles.macStepHit, {
            backgroundColor: item.id === shown.id || hovered || pressed ? colors.solid_color_tertiary : 'transparent',
            borderColor: focusedIndex === item.index ? colors.text_color_primary
              : item.id === shown.id ? colors.text_color_secondary : colors.border_color_secondary,
          }] : styles.stepHit}
        >
          {isMac && <Text size="s" color={item.id === shown.id ? 'primary' : 'secondary'}>{item.label}</Text>}
        </Pressable>
      ))}
    </View>
  );

  return (
    <View style={styles.panel}>
      <View style={styles.headerRow}>
        <View style={styles.settingLabel}>
          <Text size="s" weight="semibold">{t('chatInput.thinkingPower.specialistTitle')}</Text>
          <SettingHelpButton
            label={t('chatInput.thinkingPower.specialistHelpA11y')}
            description={t('chatInput.thinkingPower.specialistHelp')}
            expanded={showHelp}
            onPress={() => setShowHelp(value => !value)}
          />
        </View>
        <Text size="xs" color="secondary" numberOfLines={1} style={styles.headerName}>
          {shown.label}
        </Text>
      </View>
      {showHelp && (
        <Text size="xs" color="secondary" style={[styles.helpCopy, { backgroundColor: colors.solid_color_tertiary }]}>
          {t('chatInput.thinkingPower.specialistHelp')}
        </Text>
      )}

      <View
        accessibilityRole="slider"
        accessibilityLabel={t('chatInput.thinkingPower.specialistTitle')}
        accessibilityValue={{
          min: 0,
          max,
          now: shown.index,
          text: shown.label,
        }}
        onLayout={(event) => setSliderWidth(event.nativeEvent.layout.width)}
        onStartShouldSetResponder={() => true}
        onMoveShouldSetResponder={() => true}
        onResponderGrant={(event) => {
          draggingRef.current = true;
          pickFromEvent(event);
        }}
        onResponderMove={pickFromEvent}
        onResponderRelease={finishDrag}
        {...webPointer}
        style={[styles.sliderHit, { height: sliderHeight }]}
      >
        <View
          pointerEvents="none"
          style={[styles.track, { top: (sliderHeight - TRACK_HEIGHT) / 2, backgroundColor: colors.solid_color_tertiary }]}
        >
          <View
            style={[
              styles.trackFill,
              {
                backgroundColor: WORK_THINKING_TRACK_COLOR,
                width: `${fill * 100}%`,
              },
            ]}
          />
        </View>
        {WORK_THINKING_LEVELS.map((item) => (
          <View
            key={item.id}
            pointerEvents="none"
            style={[
              styles.dot,
              {
                left: SLIDER_EDGE_INSET + (item.index / max) * trackWidth,
                top: (sliderHeight - DOT_SIZE) / 2,
                opacity: sliderWidth > 0 ? 1 : 0,
                backgroundColor: item.index <= shown.index
                  ? 'rgba(255,255,255,0.85)'
                  : 'rgba(255,255,255,0.35)',
              },
              Platform.OS === 'macos' && focusedIndex === item.index && {
                width: 10, height: 10, borderRadius: 5, marginLeft: -5, top: (sliderHeight - 10) / 2,
                borderWidth: 2, borderColor: colors.text_color_primary,
              },
            ]}
          />
        ))}
        <View
          pointerEvents="none"
          style={[
            styles.thumb,
            {
              left: SLIDER_EDGE_INSET + fill * trackWidth,
              top: (sliderHeight - THUMB_SIZE) / 2,
              opacity: sliderWidth > 0 ? 1 : 0,
              backgroundColor: '#fff',
              borderColor: colors.border_color_secondary,
            },
          ]}
        />
        {/* Keep native buttons outside the slider so it owns the whole drag target. */}
        {!isMac && levelButtons}
      </View>

      <View style={styles.metaRow}>
        <Text size="xxs" color="tertiary">{t('chatInput.thinkingPower.axisLeft')}</Text>
        <Text size="xxs" color="tertiary" style={styles.metaRight}>
          {t('chatInput.thinkingPower.axisRight')}
        </Text>
      </View>
      {isMac && levelButtons}
    </View>
  );
}

WorkThinkingPowerPanel.propTypes = {
  level: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  colors: PropTypes.object.isRequired,
};

function WorkThinkingPowerTrigger({
  level,
  modelLabel,
  autoApprove = false,
  compact = false,
  isDisabled = false,
  hideTooltip = false,
  menuTrigger = false,
  onPress,
}) {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const tooltip = Platform.OS === 'macos' || Platform.OS === 'web' ? 'Choose the main model, thinking level, and specialist intelligence' : t('chatInput.thinkingPower.tooltip');
  const label = (modelLabel ? `Main model: ${modelLabel}` : t('chatInput.thinkingPower.triggerA11y', { level: level.label })) + (autoApprove ? ', Auto-approve credits on' : '');
  // Compact (mobile / narrow) is icon-only — brain reads as intelligence.
  // Desktop shows the main model; specialist intelligence lives in the menu.
  const iconName = compact ? 'brain' : 'bolt';
  const Trigger = menuTrigger ? DropdownMenu.Trigger : Pressable;

  return (
    <Tooltip label={tooltip} disabled={isDisabled || hideTooltip}>
      <Trigger
        {...(!menuTrigger ? { onPress } : {})}
        disabled={isDisabled}
        onHoverIn={() => setHovered(true)}
        onHoverOut={() => setHovered(false)}
        accessibilityRole="button"
        accessibilityLabel={label}
        accessibilityHint={tooltip}
        {...(Platform.OS === 'macos' ? {
          enableFocusRing: false,
          onFocus: () => setFocused(true),
          onBlur: () => setFocused(false),
        } : {})}
      >
        <View style={[
          styles.trigger,
          compact && styles.triggerCompact,
          (hovered || focused) && !isDisabled && { backgroundColor: colors.solid_color_tertiary },
          isDisabled && { opacity: 0.5 },
        ]}>
        <Icon
          name={iconName}
          size={compact ? 13 : 12}
          color={colors.text_color_secondary}
          solid
        />
        {!compact && (
          <Text size="s" numberOfLines={1} style={styles.triggerLabel}>
            {modelLabel || level.label}
          </Text>
        )}
        {autoApprove && <Icon name="check" size={10} color={colors.text_color_secondary} />}
        <Icon name="chevron-down" size={8} color={colors.text_color_tertiary} />
        </View>
      </Trigger>
    </Tooltip>
  );
}

WorkThinkingPowerTrigger.propTypes = {
  level: PropTypes.object.isRequired,
  modelLabel: PropTypes.string,
  autoApprove: PropTypes.bool,
  compact: PropTypes.bool,
  isDisabled: PropTypes.bool,
  hideTooltip: PropTypes.bool,
  menuTrigger: PropTypes.bool,
  onPress: PropTypes.func,
};

function WorkThinkingPowerButton({
  conversationId = null,
  collectionId = null,
  compact = false,
  isDisabled = false,
}) {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const { isXS } = useResponsiveness();
  const { width: viewportWidth, height: viewportHeight } = useWindowDimensions();
  const [open, setOpen] = useState(false);
  const [anchor, setAnchor] = useState(null);
  const [menuHeight, setMenuHeight] = useState(300);
  const triggerRef = useRef(null);
  const { addToast } = useToastStore((state) => state.actions);
  const composerThinkingPower = useCoworkerStore((s) => s.composerThinkingPower);
  const setComposerThinkingPower = useCoworkerStore((s) => s.setComposerThinkingPower);
  const setTaskModel = useCoworkerStore((s) => s.setTaskModel);
  const composerAutoApprove = useCoworkerStore((s) => s.composerAutoApprove);
  const setComposerAutoApprove = useCoworkerStore((s) => s.setComposerAutoApprove);
  const setTaskAutoApprove = useCoworkerStore((s) => s.setTaskAutoApprove);
  const [savingAutoApprove, setSavingAutoApprove] = useState(false);
  const [showAutoApproveHelp, setShowAutoApproveHelp] = useState(false);
  useEffect(() => {
    if (!open) setShowAutoApproveHelp(false);
  }, [open]);
  const taskId = useChatStore((state) => {
    if (!conversationId || conversationId === 'new') return null;
    const chat = state.chats.find((c) => c.id === conversationId);
    const raw = chat?.coworkerSession;
    if (!raw) return null;
    if (typeof raw === 'object') return String(raw._id || raw.id || '') || null;
    return String(raw);
  });
  const taskStatus = useCoworkerStore((s) => (taskId ? s.tasks[taskId]?.status : null));
  const openAbove = Platform.OS === 'macos' || Boolean(conversationId && conversationId !== 'new');
  const taskThinkingPower = useCoworkerStore((s) => (taskId ? s.tasks[taskId]?.thinkingPower : null));
  const taskAutoApprove = useCoworkerStore((s) => (taskId ? s.tasks[taskId]?.autoApprove : null));
  const modelSlug = useCoworkerStore((s) => (
    taskId ? s.tasks[taskId]?.orchestratorModel : s.composerOrchestrator?.orchestratorModel
  )) || '';
  const [models, setModels] = useState(null);
  const hasMainModelControl = Platform.OS === 'macos' || Platform.OS === 'web';
  useEffect(() => {
    if (!hasMainModelControl || !modelSlug || models) return;
    let current = true;
    coworkerApi.getOrchestratorModels()
      .then(items => { if (current) setModels(items); })
      .catch(() => {}); // Keep the selected slug visible if the catalog is unavailable.
    return () => { current = false; };
  }, [hasMainModelControl, modelSlug, models]);
  // An automatically assigned task.model is still an Auto selection.
  const modelLabel = hasMainModelControl
    ? (modelSlug ? models?.find(item => item.slug === modelSlug)?.name || modelSlug : 'Auto')
    : undefined;
  const autoApprove = taskId ? taskAutoApprove === true : composerAutoApprove;
  const changeAutoApprove = useCallback(async (enabled) => {
    if (!taskId) { setComposerAutoApprove(enabled); return; }
    setSavingAutoApprove(true);
    try { await setTaskAutoApprove(taskId, enabled); }
    catch (err) { addToast(err?.message || 'Failed to change Auto-approve', 'error'); }
    finally { setSavingAutoApprove(false); }
  }, [taskId, setComposerAutoApprove, setTaskAutoApprove, addToast]);

  const level = useMemo(() => (
    thinkingPowerById(taskThinkingPower || composerThinkingPower)
  ), [composerThinkingPower, taskThinkingPower]);

  const commitLevel = useCallback(async (next) => {
    if (!next || next.id === level.id) return;
    setComposerThinkingPower(next.id);
    if (!taskId) return;
    try {
      const out = await setTaskModel(taskId, next.id);
      if (out?.applied === 'next_turn' && isWorkActive(taskStatus)) {
        addToast(t('chatInput.thinkingPower.switchedNextTurn', { level: next.label }), 'default');
      }
    } catch (err) {
      addToast(err?.message || t('chatInput.thinkingPower.switchFailed'), 'error');
    }
  }, [addToast, level.id, setComposerThinkingPower, setTaskModel, t, taskId, taskStatus]);

  const openPopover = useCallback((event) => {
    const fromEvent = event?.currentTarget;
    const fromRef = triggerRef.current;
    const place = (rect) => {
      setAnchor(rect || null);
      setOpen(true);
    };
    const node = (fromEvent && typeof fromEvent.getBoundingClientRect === 'function')
      ? fromEvent
      : fromRef;
    if (node && typeof node.getBoundingClientRect === 'function') {
      place(node.getBoundingClientRect());
      return;
    }
    if (fromRef && typeof fromRef.measureInWindow === 'function') {
      fromRef.measureInWindow((x, y, width, height) => {
        place({
          top: y,
          left: x,
          width,
          height,
          bottom: y + height,
          right: x + width,
        });
      });
      return;
    }
    place(null);
  }, []);

  const showCompact = compact || isXS;
  const sheet = isXS || !anchor;
  const fitsBelow = anchor && viewportHeight - anchor.bottom >= menuHeight + 8;
  const fitsAbove = anchor && anchor.top >= menuHeight + 8;
  const placeAbove = fitsAbove && (openAbove || !fitsBelow);
  const menuTop = anchor ? Math.max(8, Math.min(
    placeAbove ? anchor.top - menuHeight - 6 : anchor.bottom + 6,
    viewportHeight - menuHeight - 8,
  )) : 8;
  const cardStyle = sheet
    ? styles.modalCard
    : [
      styles.popoverCard,
      { top: menuTop },
      { right: Math.max(8, viewportWidth - anchor.right) },
    ];

  const panel = <>
            {(Platform.OS === 'macos' || Platform.OS === 'web') && open && <WorkOrchestratorControls taskId={taskId} cachedModels={models} onModelsLoaded={setModels} />}
            <WorkThinkingPowerPanel
              level={level}
              onChange={commitLevel}
              colors={colors}
            />
            <View style={[styles.autoApproveRow, { borderTopColor: colors.border_color_secondary }]}>
              <View style={styles.autoApproveCopy}>
                <View style={styles.settingLabel}>
                  <Text size="s" weight="semibold">{t('chatInput.thinkingPower.autoApproveTitle')}</Text>
                  <SettingHelpButton
                    label={t('chatInput.thinkingPower.autoApproveHelpA11y')}
                    description={t('chatInput.thinkingPower.autoApproveHelp')}
                    expanded={showAutoApproveHelp}
                    onPress={() => setShowAutoApproveHelp(value => !value)}
                  />
                </View>
                <Text size="xs" color="secondary">
                  {t('chatInput.thinkingPower.autoApproveSummary')}
                </Text>
              </View>
              <Switch
                value={autoApprove}
                onValueChange={changeAutoApprove}
                disabled={savingAutoApprove || taskStatus === 'queued'}
                accessibilityLabel={t('chatInput.thinkingPower.autoApproveTitle')}
                accessibilityHint={t('chatInput.thinkingPower.autoApproveHelp')}
              />
            </View>
            {showAutoApproveHelp && (
              <Text size="xs" color="secondary" style={[styles.helpCopy, styles.autoApproveHelp, { backgroundColor: colors.solid_color_tertiary }]}>
                {t('chatInput.thinkingPower.autoApproveHelp')}
              </Text>
            )}
  </>;

  // This target does not register RCTModalHostView. Use the Mac overlay host,
  // which anchors to the trigger and handles outside clicks and Escape.
  if (Platform.OS === 'macos') return (
    <View style={styles.controls}>
      <WorkApprovalButton taskId={taskId} compact={showCompact} isDisabled={isDisabled} openAbove={openAbove} />
      <DropdownMenu.Root open={open} onOpenChange={setOpen}>
        <WorkThinkingPowerTrigger level={level} modelLabel={modelLabel} autoApprove={autoApprove} compact={showCompact}
          isDisabled={isDisabled} hideTooltip={open} menuTrigger />
        <DropdownMenu.Content side={openAbove ? 'top' : 'bottom'} align="end" sideOffset={8}
          style={{ width: Math.min(360, viewportWidth - 24), maxWidth: viewportWidth - 24 }}>
          <View style={{ paddingHorizontal: 14, paddingVertical: 8 }}>{panel}</View>
        </DropdownMenu.Content>
      </DropdownMenu.Root>
    </View>
  );

  return (
    <View style={styles.controls} ref={triggerRef} {...(Platform.OS === 'web' ? {} : { collapsable: false })}>
      <WorkProjectFolderButton taskId={taskId} collectionId={collectionId} isDisabled={isDisabled} compact={showCompact} />
      <WorkApprovalButton taskId={taskId} compact={showCompact} isDisabled={isDisabled} openAbove={openAbove} />
      <WorkThinkingPowerTrigger
        level={level}
        modelLabel={modelLabel}
        autoApprove={autoApprove}
        compact={showCompact}
        isDisabled={isDisabled}
        hideTooltip={open}
        onPress={openPopover}
      />
      <Modal
        visible={open}
        transparent
        animationType={sheet ? 'fade' : 'none'}
        onRequestClose={() => setOpen(false)}
      >
        <Pressable
          style={sheet ? styles.modalBackdrop : styles.popoverBackdrop}
          onPress={() => setOpen(false)}
        >
          <Pressable
            onPress={() => {}}
            onLayout={(event) => setMenuHeight(event.nativeEvent.layout.height)}
            style={[
              cardStyle,
              Platform.OS === 'web' && { width: Math.min(360, viewportWidth - 32), maxWidth: viewportWidth - 32 },
              {
                backgroundColor: colors.solid_color_primary,
                borderColor: colors.border_color_secondary,
              },
            ]}
          >
            {Platform.OS === 'web' ? <ScrollView style={{ maxHeight: Math.max(180, viewportHeight - 80) }} keyboardShouldPersistTaps="handled">{panel}</ScrollView> : panel}
          </Pressable>
        </Pressable>
      </Modal>
    </View>
  );
}

WorkThinkingPowerButton.propTypes = {
  conversationId: PropTypes.string,
  collectionId: PropTypes.string,
  compact: PropTypes.bool,
  isDisabled: PropTypes.bool,
};

const styles = StyleSheet.create({
  controls: { flexDirection: 'row', alignItems: 'center', flexShrink: 1, minWidth: 0 },
  autoApproveRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 14,
    borderTopWidth: 1,
    paddingTop: 10,
    marginTop: 12,
  },
  autoApproveCopy: { flex: 1, gap: 2 },
  settingLabel: { flexDirection: 'row', alignItems: 'center', gap: 4, flexShrink: 1 },
  helpButton: { width: 24, height: 24, borderRadius: 12, alignItems: 'center', justifyContent: 'center' },
  helpCopy: { lineHeight: 17, padding: 10, borderRadius: 8 },
  autoApproveHelp: { marginTop: 8 },
  trigger: {
    flexShrink: 0,
    flexDirection: 'row',
    alignItems: 'center',
    height: 34,
    paddingLeft: 10,
    paddingRight: 6,
    borderRadius: 18,
    gap: 6,
    ...(Platform.OS === 'web' ? { cursor: 'pointer', userSelect: 'none' } : {}),
  },
  triggerCompact: {
    paddingLeft: 8,
    paddingRight: 4,
    gap: 4,
  },
  triggerLabel: {
    maxWidth: 180,
  },
  panel: {
    gap: 6,
    width: '100%',
    minWidth: 0,
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
  },
  headerName: {
    flexShrink: 1,
    textAlign: 'right',
  },
  sliderHit: {
    justifyContent: 'center',
    overflow: 'visible',
    position: 'relative',
    ...(Platform.OS === 'web' ? { cursor: 'pointer', userSelect: 'none', touchAction: 'none' } : {}),
  },
  stepRow: {
    ...StyleSheet.absoluteFillObject,
    flexDirection: 'row',
  },
  stepHit: {
    flex: 1,
    height: '100%',
  },
  macStepRow: { flexDirection: 'row', gap: 8, marginTop: 4 },
  macStepHit: {
    flex: 1,
    minHeight: 44,
    paddingHorizontal: 8,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderRadius: 10,
  },
  track: {
    position: 'absolute',
    left: SLIDER_EDGE_INSET,
    right: SLIDER_EDGE_INSET,
    height: TRACK_HEIGHT,
    borderRadius: TRACK_HEIGHT / 2,
    overflow: 'visible',
    justifyContent: 'center',
  },
  trackFill: {
    position: 'absolute',
    left: 0,
    top: 0,
    bottom: 0,
    borderRadius: TRACK_HEIGHT / 2,
  },
  dot: {
    position: 'absolute',
    width: DOT_SIZE,
    height: DOT_SIZE,
    borderRadius: DOT_SIZE / 2,
    marginLeft: -DOT_SIZE / 2,
  },
  thumb: {
    position: 'absolute',
    width: THUMB_SIZE,
    height: THUMB_SIZE,
    borderRadius: THUMB_SIZE / 2,
    marginLeft: -THUMB_SIZE / 2,
    borderWidth: StyleSheet.hairlineWidth,
    ...Platform.select({
      web: { boxShadow: '0 1px 4px rgba(0,0,0,0.35)' },
      default: {
        shadowColor: '#000',
        shadowOpacity: 0.25,
        shadowRadius: 3,
        shadowOffset: { width: 0, height: 1 },
        elevation: 2,
      },
    }),
  },
  metaRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: 8,
  },
  metaRight: {
    textAlign: 'right',
    flexShrink: 1,
  },
  modalBackdrop: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.4)',
    justifyContent: 'flex-end',
    padding: 16,
  },
  modalCard: {
    borderRadius: 16,
    borderWidth: 1,
    padding: 14,
    marginBottom: 24,
  },
  popoverBackdrop: {
    flex: 1,
    backgroundColor: 'transparent',
  },
  popoverCard: {
    position: 'absolute',
    width: 300,
    borderRadius: 16,
    borderWidth: 1,
    padding: 12,
    ...Platform.select({
      web: { boxShadow: '0 10px 38px -10px rgba(0,0,0,0.45), 0 10px 20px -15px rgba(0,0,0,0.25)' },
      default: {
        shadowColor: '#000',
        shadowOpacity: 0.35,
        shadowRadius: 16,
        shadowOffset: { width: 0, height: 8 },
        elevation: 8,
      },
    }),
  },
});

export default React.memo(WorkThinkingPowerButton);
